import { useEffect, useReducer } from "react";
import { Download } from "lucide-react";
import moment from "moment-timezone";
import { Button } from "@/components/ui/button";
import { DateRangePicker, type DateRange } from "@/components/ui/date_picker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { formatDateWithoutTimezoneToFormat } from "@/lib/api/date";
import {
  exportSentOtcInvoicesReport,
  type SentOtcInvoicesExportInvalidInvoice,
} from "@/lib/api/otcInvoices";
import { downloadFromUrl } from "@/lib/browser/download";
import { ExportSentOtcInvoicesReportRequestValidation } from "@/lib/validation/otcInvoices";
import { prettifyCode } from "./InvoiceActionDialogs";

const API_DATE_FORMAT = "YYYY-MM-DD";

type GeneratedExportReport = {
  link: string;
  filename: string;
};

type ExportInvoicesState = {
  dateRange: DateRange;
  isGenerating: boolean;
  error: string | null;
  skipped: SentOtcInvoicesExportInvalidInvoice[];
  generatedReport: GeneratedExportReport | null;
};

/**
 * `generationStarted` opens a generation and `generationFinished` closes it, so
 * every exit path (validation, request failure, download failure, success)
 * leaves `isGenerating` cleared via a `finally` rather than each branch having
 * to remember to.
 */
type ExportInvoicesAction =
  | { type: "reset" }
  | { type: "changeDateRange"; dateRange: DateRange }
  | { type: "generationStarted" }
  | { type: "generationSucceeded"; report: GeneratedExportReport; skipped: SentOtcInvoicesExportInvalidInvoice[] }
  | { type: "generationFailed"; error: string }
  | { type: "generationFinished" };

/**
 * Picker Date (whose local Y/M/D is what the user chose) → `YYYY-MM-DD` portal
 * date, taking the local calendar day directly rather than through an absolute
 * instant, so the day the user picked is the day the backend exports.
 */
function apiDate(date: Date): string {
  return formatDateWithoutTimezoneToFormat(date, API_DATE_FORMAT);
}

function todayRange(): DateRange {
  const today = moment().startOf("day").toDate();
  return { from: today, to: today };
}

function initialExportInvoicesState(): ExportInvoicesState {
  return {
    dateRange: todayRange(),
    isGenerating: false,
    error: null,
    skipped: [],
    generatedReport: null,
  };
}

function exportInvoicesReducer(
  state: ExportInvoicesState,
  action: ExportInvoicesAction,
): ExportInvoicesState {
  switch (action.type) {
    case "reset":
      return initialExportInvoicesState();
    case "changeDateRange":
      return { ...state, dateRange: action.dateRange };
    case "generationStarted":
      return { ...state, isGenerating: true, error: null, skipped: [], generatedReport: null };
    case "generationSucceeded":
      return { ...state, skipped: action.skipped, generatedReport: action.report };
    case "generationFailed":
      return { ...state, error: action.error };
    case "generationFinished":
      return { ...state, isGenerating: false };
  }
}

function errorDescription(result: { message?: string; data?: unknown }): string {
  const context = result.data === undefined ? "" : ` ${JSON.stringify(result.data)}`;
  return `${result.message || "The request failed."}${context}`;
}

/**
 * Exports sent OTC invoices as the Agtech import file over an inclusive invoice
 * date range. The backend generates and stores the file, then hands back the
 * download link and the filename it chose; the browser download starts as soon
 * as it is generated.
 *
 * Invoices the backend cannot import (for example a legacy row whose customer
 * has no usable payment type) are left out of the file and listed here by
 * invoice number so the skipped rows are never silently dropped.
 */
export function ExportInvoicesDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { toast } = useToast();
  const [state, dispatch] = useReducer(exportInvoicesReducer, undefined, initialExportInvoicesState);

  useEffect(() => {
    if (!open) return;
    dispatch({ type: "reset" });
  }, [open]);

  const startDate = state.dateRange.from ? apiDate(state.dateRange.from) : undefined;
  const endDate = state.dateRange.to ? apiDate(state.dateRange.to) : undefined;
  const isRangeValid = startDate !== undefined && endDate !== undefined && startDate <= endDate;

  const runExport = async (): Promise<void> => {
    if (state.isGenerating || !isRangeValid) return;

    const parsed = ExportSentOtcInvoicesReportRequestValidation.safeParse({
      start_date: startDate,
      end_date: endDate,
    });
    if (!parsed.success) {
      dispatch({
        type: "generationFailed",
        error: parsed.error.issues[0]?.message ?? "Select a valid invoice date range.",
      });
      return;
    }

    dispatch({ type: "generationStarted" });
    try {
      const result = await exportSentOtcInvoicesReport(parsed.data);
      if (!result.success) {
        dispatch({ type: "generationFailed", error: errorDescription(result) });
        return;
      }

      const { invalid_invoices: invalidInvoices, valid_invoices_numbers: validInvoiceNumbers, report } = result.data;
      dispatch({
        type: "generationSucceeded",
        report: { link: report.link, filename: report.filename },
        skipped: invalidInvoices,
      });

      try {
        await downloadFromUrl(report.link, report.filename);
      } catch (downloadError: unknown) {
        console.error("Failed to download sent OTC invoices export", downloadError);
        toast({
          title: "Report generated but the download failed",
          description: "Use the download link below to try again.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: `Exported ${validInvoiceNumbers.length} invoice${validInvoiceNumbers.length === 1 ? "" : "s"}`,
        description: invalidInvoices.length > 0
          ? `${invalidInvoices.length} invoice${invalidInvoices.length === 1 ? " was" : "s were"} skipped.`
          : undefined,
      });
    } catch (requestError: unknown) {
      dispatch({
        type: "generationFailed",
        error: requestError instanceof Error ? requestError.message : "The request failed.",
      });
    } finally {
      dispatch({ type: "generationFinished" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen: boolean) => { if (!nextOpen && !state.isGenerating) onClose(); }}>
      <DialogContent className="sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>Export Sent Invoices</DialogTitle>
          <DialogDescription>
            Download the Agtech import file of sent OTC invoices by invoice date. Both dates are inclusive.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label>Invoice Date Range</Label>
            <DateRangePicker
              start={state.dateRange.from}
              end={state.dateRange.to}
              onChange={(range: DateRange | undefined) => dispatch({ type: "changeDateRange", dateRange: range ?? { from: undefined, to: undefined } })}
              fromYear={2020}
              toYear={moment().add(2, "years").year()}
              placeholder="Select an invoice date range"
            />
            {!isRangeValid && (
              <p className="text-xs text-destructive">Select a start date on or before the end date.</p>
            )}
          </div>
          {state.error ? <p className="text-sm text-destructive">{state.error}</p> : null}
          {state.skipped.length > 0 && (
            <div className="max-h-[40vh] space-y-2 overflow-y-auto rounded-md border border-red-200 bg-red-50 p-3">
              <p className="font-medium text-red-900">
                {state.skipped.length} invoice{state.skipped.length === 1 ? "" : "s"} skipped
              </p>
              <p className="text-xs text-red-700">
                These invoices were left out of the export.
              </p>
              <ul className="list-disc space-y-1 pl-5 text-sm text-red-900">
                {state.skipped.map((invoice: SentOtcInvoicesExportInvalidInvoice) => (
                  <li key={invoice.id}>
                    {invoice.invoice_number}
                    {invoice.reasons.length > 0 ? ` (${invoice.reasons.map(prettifyCode).join(", ")})` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {state.generatedReport ? (
            <a href={state.generatedReport.link} download={state.generatedReport.filename} className="text-sm text-blue-600 underline">
              Download {state.generatedReport.filename} again
            </a>
          ) : null}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={state.isGenerating}>Close</Button>
          <Button type="button" onClick={runExport} disabled={!isRangeValid || state.isGenerating}>
            <Download className="mr-2 h-4 w-4" />
            {state.isGenerating ? "Generating..." : "Generate Report"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
